/**
 * Centralized Document & Side/Page Validation Utility
 * PairUpToday Platform
 *
 * Enforces mandatory completion of all required document sides (front, back, data pages)
 * before allowing companion/client profile submission or verification progression.
 *
 * Rules:
 * - Aadhaar Card: Both Front and Back sides are strictly mandatory.
 * - PAN Card: Front side is mandatory; back side is not required.
 * - Driving Licence: Both Front and Back sides are strictly mandatory.
 * - Passport: Page 1 (Data & Photo) and Page 2 (Address & Family) are strictly mandatory.
 * - Voter ID: Both Front and Back sides are strictly mandatory.
 */

export type DocumentSide = 'front' | 'back' | 'page1' | 'page2';
export type DocumentUploadState = 'missing' | 'uploading' | 'uploaded';

export type CompanionProfileLifecycleStatus =
  | 'draft'
  | 'documents_incomplete'
  | 'documents_uploaded'
  | 'under_review'
  | 'verified'
  | 'rejected'
  | 'suspended';

export type SupportedDocumentType =
  | 'aadhaar'
  | 'pan'
  | 'driving_license'
  | 'passport'
  | 'voter_id';

export type KycDocumentTypeUpper =
  | 'AADHAAR'
  | 'PAN'
  | 'DRIVING_LICENSE'
  | 'PASSPORT'
  | 'VOTER_ID';

export interface DocumentPartMetadata {
  side: DocumentSide;
  key: 'front' | 'back';
  label: string;
  shortLabel: string;
  required: boolean;
  helpText: string;
  description?: string;
}

export interface DocumentPartConfig {
  side: 'front' | 'back';
  label: string;
  required: boolean;
  helpText?: string;
}

export interface ConfiguredDocumentRequirement {
  id: string; // e.g. 'aadhaar', 'pan', 'driving_license', 'voter_id', 'passport'
  title: string;
  badge?: string;
  required: boolean;
  parts: DocumentPartConfig[];
  description?: string;
  isConditional?: boolean;
  conditionDescription?: string;
  conditionMet?: (context: any) => boolean;
}

export interface DocumentRequirement {
  id: SupportedDocumentType;
  title: string;
  subtitle: string;
  badge: string;
  sidesRequired: 'BOTH' | 'FRONT_ONLY';
  requiresBack: boolean;
  idFormatHint: string;
  parts: DocumentPartMetadata[];
}

export interface UploadedPartData {
  uploaded: boolean;
  url?: string;
  downloadUrl?: string;
  storagePath?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt?: string;
  state?: DocumentUploadState;
  error?: string;
}

export interface CompanionDocumentEntry {
  idNumber?: string;
  legalName?: string;
  status?: string;
  front?: UploadedPartData | string | any;
  back?: UploadedPartData | string | any;
  [key: string]: any;
}

export interface MissingDocumentSideItem {
  documentId: string;
  documentTitle: string;
  side: DocumentSide;
  label: string;
  reason: string;
}

export interface UploadingDocumentSideItem {
  documentId: string;
  documentTitle: string;
  side: DocumentSide;
  label: string;
}

export interface DocumentSidesValidationResult {
  isValid: boolean;
  documentType: SupportedDocumentType;
  title: string;
  missingSides: MissingDocumentSideItem[];
  uploadingSides: UploadingDocumentSideItem[];
  completedSides: DocumentSide[];
  totalRequiredSides: number;
  completedSidesCount: number;
  errors: string[];
}

export interface ProfileSubmissionValidationContext {
  // Primary identity document
  documentType?: string;
  front?: any; // URL string, UploadedPartData, File, storagePath, etc.
  back?: any;
  // Multi-document mapping (optional)
  documents?: Record<string, any>;
  // Optional profile attributes
  legalName?: string;
  idNumber?: string;
  selfie?: any;
  requireSelfie?: boolean;
  selectedServices?: string[];
  isDriverService?: boolean;
  isProfileInfoComplete?: boolean;
  currentAdminVerdict?: 'verified' | 'rejected' | 'suspended' | null;
  verificationStarted?: boolean;
}

export interface ProfileSubmissionValidationResult {
  canSubmit: boolean;
  isValid: boolean;
  totalRequiredSides: number;
  completedSidesCount: number;
  completionPercentage: number;
  missingMandatorySides: MissingDocumentSideItem[];
  uploadingSides: UploadingDocumentSideItem[];
  errors: string[];
  summaryMessage: string;
  lifecycleStatus: CompanionProfileLifecycleStatus;
}

export interface DocumentValidationResult {
  isValid: boolean;
  profileStatus: CompanionProfileLifecycleStatus;
  missingItems: {
    documentId: string;
    documentTitle: string;
    side: 'front' | 'back';
    label: string;
    reason: string;
  }[];
  uploadingItems: {
    documentId: string;
    documentTitle: string;
    side: 'front' | 'back';
    label: string;
  }[];
  validationErrors: string[];
  summaryMessage: string;
}

/**
 * Standard registry defining document rules and mandatory sides/pages.
 */
export const DOCUMENT_REQUIREMENTS_REGISTRY: Record<SupportedDocumentType, DocumentRequirement> = {
  aadhaar: {
    id: 'aadhaar',
    title: 'Aadhaar Card',
    subtitle: '12-digit UIDAI National Identity Card',
    badge: 'Front + Back Required (দুই পাশ)',
    sidesRequired: 'BOTH',
    requiresBack: true,
    idFormatHint: 'e.g. 5412 8901 2345',
    parts: [
      {
        side: 'front',
        key: 'front',
        label: 'Aadhaar Front Side *',
        shortLabel: 'Front Side',
        required: true,
        helpText: 'Must show clear photograph, full legal name, DOB, and 12-digit Aadhaar number.',
      },
      {
        side: 'back',
        key: 'back',
        label: 'Aadhaar Back Side *',
        shortLabel: 'Back Side',
        required: true,
        helpText: 'Must show permanent residential address and valid QR code.',
      },
    ],
  },
  pan: {
    id: 'pan',
    title: 'PAN Card',
    subtitle: 'Permanent Account Number — Income Tax Dept of India',
    badge: 'Front Only (শুধু Front পাশ)',
    sidesRequired: 'FRONT_ONLY',
    requiresBack: false,
    idFormatHint: 'e.g. ABCDE1234F',
    parts: [
      {
        side: 'front',
        key: 'front',
        label: 'PAN Front Side *',
        shortLabel: 'Front Side',
        required: true,
        helpText: 'Must show clear photograph, 10-digit PAN alphanumeric number, and father’s name.',
      },
    ],
  },
  driving_license: {
    id: 'driving_license',
    title: 'Driving Licence',
    subtitle: 'State Transport Department Government of India',
    badge: 'Front + Back Required (দুই পাশ)',
    sidesRequired: 'BOTH',
    requiresBack: true,
    idFormatHint: 'e.g. DL-01-20220019284',
    parts: [
      {
        side: 'front',
        key: 'front',
        label: 'Driving Licence Front *',
        shortLabel: 'Front Side',
        required: true,
        helpText: 'Must clearly show licence number, valid expiry date, and your photograph.',
      },
      {
        side: 'back',
        key: 'back',
        label: 'Driving Licence Back *',
        shortLabel: 'Back Side',
        required: true,
        helpText: 'Must show vehicle classes authorized (MCWG, LMV) and transport endorsements.',
      },
    ],
  },
  passport: {
    id: 'passport',
    title: 'Passport',
    subtitle: 'Republic of India — Ministry of External Affairs',
    badge: 'Page 1 + Page 2 Required (দুই পাতা)',
    sidesRequired: 'BOTH',
    requiresBack: true,
    idFormatHint: 'e.g. Z1234567',
    parts: [
      {
        side: 'front',
        key: 'front',
        label: 'Passport Page 1: Data & Photo Page *',
        shortLabel: 'Page 1 (Data & Photo)',
        required: true,
        helpText: 'Must show photograph, passport number, validity dates, and machine-readable MRZ lines.',
      },
      {
        side: 'back',
        key: 'back',
        label: 'Passport Page 2: Address & Parents Page *',
        shortLabel: 'Page 2 (Address & Parents)',
        required: true,
        helpText: 'Must show permanent residential address and parent/spouse names.',
      },
    ],
  },
  voter_id: {
    id: 'voter_id',
    title: 'Voter ID (EPIC)',
    subtitle: 'Election Commission of India Identity Card',
    badge: 'Front + Back Required (দুই পাশ)',
    sidesRequired: 'BOTH',
    requiresBack: true,
    idFormatHint: 'e.g. ABC1234567',
    parts: [
      {
        side: 'front',
        key: 'front',
        label: 'Voter ID Front *',
        shortLabel: 'Front Side',
        required: true,
        helpText: 'Must show photograph, legal name, and EPIC alphanumeric number.',
      },
      {
        side: 'back',
        key: 'back',
        label: 'Voter ID Back *',
        shortLabel: 'Back Side',
        required: true,
        helpText: 'Must show permanent address and assembly constituency details.',
      },
    ],
  },
};

/**
 * Universal Registry of Document Configurations (compatible with companion profile schemas).
 */
export const COMPANION_DOCUMENT_REGISTRY: Record<string, ConfiguredDocumentRequirement> = {
  aadhaar: {
    id: 'aadhaar',
    title: 'Aadhaar Card',
    badge: 'Front + Back Required',
    required: true,
    description: '12-digit UIDAI National Identity Card',
    parts: [
      {
        side: 'front',
        label: 'Aadhaar Front Side *',
        required: true,
        helpText: 'Must show clear face, full legal name, DOB, and 12-digit Aadhaar number',
      },
      {
        side: 'back',
        label: 'Aadhaar Back Side *',
        required: true,
        helpText: 'Must show permanent residential address and valid QR code',
      },
    ],
  },
  pan: {
    id: 'pan',
    title: 'PAN Card',
    badge: 'Front Only',
    required: false,
    description: 'Permanent Account Number (Income Tax Dept)',
    parts: [
      {
        side: 'front',
        label: 'PAN Front Side *',
        required: true,
        helpText: 'Must show clear photograph, 10-character PAN number, and father’s name',
      },
    ],
  },
  driving_license: {
    id: 'driving_license',
    title: 'Driving Licence',
    badge: 'Front + Back Required',
    required: false,
    isConditional: true,
    conditionDescription: 'Mandatory if Driver category is selected in services',
    conditionMet: (ctx) => Boolean(ctx?.selectedServices?.includes('Driver') || ctx?.isDriverService),
    description: 'Government Issued Driver Licence (Transport Dept)',
    parts: [
      {
        side: 'front',
        label: 'Driving Licence Front *',
        required: true,
        helpText: 'Must clearly show licence number, valid expiry date, and your photograph',
      },
      {
        side: 'back',
        label: 'Driving Licence Back *',
        required: true,
        helpText: 'Must show vehicle classes authorized (MCWG, LMV) and badge endorsement',
      },
    ],
  },
  voter_id: {
    id: 'voter_id',
    title: 'Voter ID (EPIC)',
    badge: 'Front + Back Required',
    required: false,
    description: 'Election Commission of India Identity Card',
    parts: [
      {
        side: 'front',
        label: 'Voter ID Front *',
        required: true,
        helpText: 'Must show photograph, legal name, and EPIC alphanumeric number',
      },
      {
        side: 'back',
        label: 'Voter ID Back *',
        required: true,
        helpText: 'Must show permanent address and assembly constituency details',
      },
    ],
  },
  passport: {
    id: 'passport',
    title: 'Passport',
    badge: 'Data Page + Address Page',
    required: false,
    description: 'Republic of India — Ministry of External Affairs',
    parts: [
      {
        side: 'front',
        label: 'Passport Photo & MRZ Page *',
        required: true,
        helpText: 'Page 1 must show photograph, passport number, validity, and MRZ lines',
      },
      {
        side: 'back',
        label: 'Passport Address Page *',
        required: true,
        helpText: 'Page 2 must show permanent residential address and parent/spouse names',
      },
    ],
  },
};

/**
 * Normalizes any raw document type string (case-insensitive, handles aliases)
 * e.g. "Aadhaar Card" -> "aadhaar", "AADHAAR" -> "aadhaar", "Driving Licence" -> "driving_license"
 */
export function normalizeDocumentType(rawType?: string): SupportedDocumentType {
  if (!rawType) return 'aadhaar';
  const clean = rawType.toLowerCase().trim().replace(/[-\s]+/g, '_');

  if (clean.includes('aadhaar') || clean.includes('adhar') || clean.includes('uidai')) {
    return 'aadhaar';
  }
  if (clean.includes('pan')) {
    return 'pan';
  }
  if (clean.includes('driv') || clean.includes('dl') || clean.includes('licen')) {
    return 'driving_license';
  }
  if (clean.includes('pass') || clean.includes('ppt')) {
    return 'passport';
  }
  if (clean.includes('voter') || clean.includes('epic') || clean.includes('election')) {
    return 'voter_id';
  }
  return 'aadhaar';
}

/**
 * Alias for normalizeDocumentType for backward compatibility.
 */
export const normalizeDocumentTypeId = normalizeDocumentType;

/**
 * Retrieves the requirement configuration for a given document type.
 */
export function getDocumentRequirements(documentType?: string): DocumentRequirement {
  const normalized = normalizeDocumentType(documentType);
  return DOCUMENT_REQUIREMENTS_REGISTRY[normalized];
}

/**
 * Checks whether the back side/page of a given document type is strictly mandatory.
 */
export function isBackSideRequired(documentType?: string): boolean {
  const req = getDocumentRequirements(documentType);
  return req.requiresBack;
}

/**
 * Returns the list of mandatory side keys ('front' | 'back') for a given document type.
 */
export function getMandatoryDocumentSides(documentType?: string): ('front' | 'back')[] {
  const req = getDocumentRequirements(documentType);
  return req.parts.filter((p) => p.required).map((p) => p.key);
}

/**
 * Robust check determining whether a document side/page upload value is valid and complete.
 * Handles:
 * - URL strings (https://, http://, data:image, blob:)
 * - Objects with url, downloadUrl, storagePath, frontFileKey, backFileKey
 * - State flags ('uploaded' vs 'uploading' vs 'missing')
 * - Binary/File representations
 */
export function hasValidUploadedData(data: any): boolean {
  if (data === null || data === undefined) return false;

  // If directly a string
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) return false;
    // Disallow dummy placeholders
    if (
      trimmed === 'pdf_placeholder' ||
      trimmed === 'placeholder' ||
      trimmed === 'null' ||
      trimmed === 'undefined'
    ) {
      return false;
    }
    // Accept valid URLs, Base64 data strings, or valid file references
    return (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('data:image/') ||
      trimmed.startsWith('data:application/pdf') ||
      trimmed.startsWith('blob:') ||
      trimmed.length >= 6
    );
  }

  // If File or Blob object in browser
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return data.size > 0;
  }

  // If structured object (UploadedPartData, KycDocumentEntity, DocumentSideData)
  if (typeof data === 'object') {
    // If explicitly marked as uploading, it is NOT complete yet
    if (data.state === 'uploading' || data.isUploading === true) {
      return false;
    }

    // Check specific fields
    const directCandidate =
      data.url ||
      data.downloadUrl ||
      data.storagePath ||
      data.fileKey ||
      data.frontFileKey ||
      data.backFileKey ||
      data.frontImageUrl ||
      data.backImageUrl ||
      data.previewUrl ||
      data.frontPreviewUrl ||
      data.backPreviewUrl;

    if (directCandidate && typeof directCandidate === 'string' && directCandidate.trim().length > 5) {
      return true;
    }

    // Check boolean flag if supported by model
    if (data.uploaded === true && (data.fileName || data.fileSize || data.url || data.storagePath)) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a document part is currently in the process of uploading.
 */
export function isPartUploading(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  return data.state === 'uploading' || data.isUploading === true || data.uploading === true;
}

/**
 * Validates a single document's uploaded sides against its mandatory requirement specifications.
 * Enforces:
 * - If Both Front & Back required: BOTH must be present and valid.
 * - If Front Only required: Front must be valid; back is not required.
 */
export function validateDocumentSides(
  documentType: string,
  sides: {
    front?: any;
    back?: any;
    frontImageUrl?: any;
    backImageUrl?: any;
    frontFileKey?: any;
    backFileKey?: any;
    [key: string]: any;
  }
): DocumentSidesValidationResult {
  const req = getDocumentRequirements(documentType);
  const missingSides: MissingDocumentSideItem[] = [];
  const uploadingSides: UploadingDocumentSideItem[] = [];
  const completedSides: DocumentSide[] = [];
  const errors: string[] = [];

  // Extract front data candidate
  const frontData =
    sides?.front ??
    sides?.frontImageUrl ??
    sides?.frontFileKey ??
    sides?.frontImage ??
    sides?.page1;

  // Extract back data candidate
  const backData =
    sides?.back ??
    sides?.backImageUrl ??
    sides?.backFileKey ??
    sides?.backImage ??
    sides?.page2;

  for (const part of req.parts) {
    const partData = part.key === 'front' ? frontData : backData;

    if (isPartUploading(partData)) {
      uploadingSides.push({
        documentId: req.id,
        documentTitle: req.title,
        side: part.side,
        label: part.label,
      });
      errors.push(`${part.label} is currently uploading. Please wait for completion.`);
      continue;
    }

    const isComplete = hasValidUploadedData(partData);

    if (isComplete) {
      completedSides.push(part.side);
    } else if (part.required) {
      missingSides.push({
        documentId: req.id,
        documentTitle: req.title,
        side: part.side,
        label: part.label,
        reason: `Mandatory ${part.shortLabel} of ${req.title} has not been uploaded.`,
      });

      const message =
        part.key === 'front'
          ? `Please upload the ${part.shortLabel} of your ${req.title}.`
          : `Please upload the ${part.shortLabel} of your ${req.title}.`;
      errors.push(message);
    }
  }

  const totalRequiredSides = req.parts.filter((p) => p.required).length;
  const isValid = missingSides.length === 0 && uploadingSides.length === 0;

  return {
    isValid,
    documentType: req.id,
    title: req.title,
    missingSides,
    uploadingSides,
    completedSides,
    totalRequiredSides,
    completedSidesCount: completedSides.length,
    errors,
  };
}

/**
 * Custom Error thrown when mandatory document sides/pages are incomplete upon submission.
 */
export class DocumentValidationError extends Error {
  public readonly missingSides: MissingDocumentSideItem[];
  public readonly uploadingSides: UploadingDocumentSideItem[];
  public readonly validationErrors: string[];

  constructor(
    message: string,
    missingSides: MissingDocumentSideItem[] = [],
    uploadingSides: UploadingDocumentSideItem[] = [],
    validationErrors: string[] = []
  ) {
    super(message);
    this.name = 'DocumentValidationError';
    this.missingSides = missingSides;
    this.uploadingSides = uploadingSides;
    this.validationErrors = validationErrors;
    Object.setPrototypeOf(this, DocumentValidationError.prototype);
  }
}

/**
 * Resolves active document requirements based on context (e.g. selected primary ID & services)
 */
export function resolveActiveDocumentRequirements(context?: {
  selectedPrimaryDocType?: string;
  selectedServices?: string[];
  isDriverService?: boolean;
}): ConfiguredDocumentRequirement[] {
  const primaryId = normalizeDocumentType(context?.selectedPrimaryDocType || 'aadhaar');
  const isDriver = Boolean(
    context?.selectedServices?.includes('Driver') ||
    context?.isDriverService
  );

  const configs: ConfiguredDocumentRequirement[] = [];

  // 1. Primary Identity Document (Always Mandatory)
  const primaryConfig = COMPANION_DOCUMENT_REGISTRY[primaryId] || {
    id: primaryId,
    title: context?.selectedPrimaryDocType || 'Government ID Card',
    badge: 'Front + Back Required',
    required: true,
    parts: [
      { side: 'front' as const, label: `${context?.selectedPrimaryDocType || 'Govt ID'} Front *`, required: true },
      { side: 'back' as const, label: `${context?.selectedPrimaryDocType || 'Govt ID'} Back *`, required: true },
    ],
  };

  configs.push({
    ...primaryConfig,
    required: true, // Primary selected ID is ALWAYS mandatory
  });

  // 2. Conditional Driving Licence (Mandatory if Driver category is selected)
  if (isDriver && primaryId !== 'driving_license') {
    const dlConfig = COMPANION_DOCUMENT_REGISTRY['driving_license'];
    if (dlConfig) {
      configs.push({
        ...dlConfig,
        required: true,
        isConditional: true,
      });
    }
  }

  return configs;
}

/**
 * Calculates the Companion Profile Lifecycle Status
 */
export function calculateCompanionProfileStatus(input: {
  isValid: boolean;
  missingCount: number;
  uploadingCount: number;
  isProfileInfoComplete?: boolean;
  verificationStarted?: boolean;
  currentAdminVerdict?: 'verified' | 'rejected' | 'suspended' | null;
}): CompanionProfileLifecycleStatus {
  if (input.currentAdminVerdict === 'rejected') return 'rejected';
  if (input.currentAdminVerdict === 'suspended') return 'suspended';
  if (input.currentAdminVerdict === 'verified') return 'verified';

  if (input.isProfileInfoComplete === false) {
    return 'draft';
  }

  if (!input.isValid || input.missingCount > 0 || input.uploadingCount > 0) {
    return 'documents_incomplete';
  }

  if (input.verificationStarted) {
    return 'under_review';
  }

  return 'documents_uploaded';
}

/**
 * Validates companion documents map against configured requirements.
 */
export function validateCompanionDocuments(
  documents: Record<string, CompanionDocumentEntry | undefined>,
  context?: {
    isProfileInfoComplete?: boolean;
    selectedPrimaryDocType?: string;
    selectedServices?: string[];
    isDriverService?: boolean;
    currentAdminVerdict?: 'verified' | 'rejected' | 'suspended' | null;
    verificationStarted?: boolean;
  }
): DocumentValidationResult {
  const activeRequirements = resolveActiveDocumentRequirements(context);
  const missingItems: DocumentValidationResult['missingItems'] = [];
  const uploadingItems: DocumentValidationResult['uploadingItems'] = [];
  const validationErrors: string[] = [];

  for (const req of activeRequirements) {
    if (!req.required) {
      continue;
    }

    const docEntry = documents[req.id] || documents[normalizeDocumentType(req.id)];

    for (const part of req.parts) {
      if (!part.required) continue;

      const partData = part.side === 'front' ? docEntry?.front : docEntry?.back;

      if (isPartUploading(partData)) {
        uploadingItems.push({
          documentId: req.id,
          documentTitle: req.title,
          side: part.side,
          label: part.label,
        });
        validationErrors.push(`${part.label} is currently uploading. Please wait for completion.`);
        continue;
      }

      const hasValidRef = hasValidUploadedData(partData);

      if (!hasValidRef) {
        missingItems.push({
          documentId: req.id,
          documentTitle: req.title,
          side: part.side,
          label: part.label,
          reason: `Required ${part.side.toUpperCase()} upload is missing`,
        });

        const errorMsg =
          part.side === 'front'
            ? `Please upload the front side of your ${req.title}.`
            : `Please upload the back side of your ${req.title}.`;
        validationErrors.push(errorMsg);
      }
    }
  }

  const isValid = missingItems.length === 0 && uploadingItems.length === 0;

  const profileStatus = calculateCompanionProfileStatus({
    isValid,
    missingCount: missingItems.length,
    uploadingCount: uploadingItems.length,
    isProfileInfoComplete: context?.isProfileInfoComplete ?? true,
    verificationStarted: context?.verificationStarted,
    currentAdminVerdict: context?.currentAdminVerdict,
  });

  let summaryMessage = 'All required documents verified successfully.';
  if (uploadingItems.length > 0) {
    summaryMessage = 'Please wait for your document uploads to complete before submitting.';
  } else if (missingItems.length > 0) {
    summaryMessage = `Please upload all required documents before continuing (${missingItems.length} missing).`;
  }

  return {
    isValid,
    profileStatus,
    missingItems,
    uploadingItems,
    validationErrors,
    summaryMessage,
  };
}

/**
 * Centralized profile document submission validation.
 *
 * Enforces completion of all mandatory document sides/pages across:
 * 1. Primary selected ID document (Aadhaar, PAN, DL, Passport, Voter ID)
 * 2. Mandatory driving license if Driver services are offered
 * 3. Auxiliary requirements (e.g. selfie if requested)
 *
 * Returns a comprehensive, user-friendly validation report.
 */
export function validateProfileSubmission(
  context: ProfileSubmissionValidationContext
): ProfileSubmissionValidationResult {
  const missingMandatorySides: MissingDocumentSideItem[] = [];
  const uploadingSides: UploadingDocumentSideItem[] = [];
  const errors: string[] = [];

  let totalRequiredSides = 0;
  let completedSidesCount = 0;

  // 1. Validate Primary Document
  const primaryType = normalizeDocumentType(context.documentType || 'aadhaar');
  const primaryDocEntry = context.documents?.[primaryType] || {
    front: context.front,
    back: context.back,
  };

  const primaryValidation = validateDocumentSides(primaryType, primaryDocEntry);
  totalRequiredSides += primaryValidation.totalRequiredSides;
  completedSidesCount += primaryValidation.completedSidesCount;

  missingMandatorySides.push(...primaryValidation.missingSides);
  uploadingSides.push(...primaryValidation.uploadingSides);
  errors.push(...primaryValidation.errors);

  // 2. Validate Conditional Requirements: Driving Licence for Drivers
  const isDriver = Boolean(
    context.isDriverService ||
      context.selectedServices?.some((s) => s.toLowerCase().includes('driver'))
  );

  if (isDriver && primaryType !== 'driving_license') {
    const dlDocEntry = context.documents?.['driving_license'] || {};
    const dlValidation = validateDocumentSides('driving_license', dlDocEntry);
    totalRequiredSides += dlValidation.totalRequiredSides;
    completedSidesCount += dlValidation.completedSidesCount;

    missingMandatorySides.push(...dlValidation.missingSides);
    uploadingSides.push(...dlValidation.uploadingSides);
    errors.push(...dlValidation.errors);
  }

  // 3. Optional selfie validation if required
  if (context.requireSelfie) {
    totalRequiredSides += 1;
    if (isPartUploading(context.selfie)) {
      uploadingSides.push({
        documentId: 'selfie',
        documentTitle: 'Live Selfie',
        side: 'front',
        label: 'Biometric Face Selfie',
      });
      errors.push('Selfie verification is currently uploading.');
    } else if (hasValidUploadedData(context.selfie)) {
      completedSidesCount += 1;
    } else {
      missingMandatorySides.push({
        documentId: 'selfie',
        documentTitle: 'Live Selfie',
        side: 'front',
        label: 'Biometric Face Selfie',
        reason: 'Live selfie photo is required to verify identity match.',
      });
      errors.push('Please take or upload a live verification selfie.');
    }
  }

  // Check additional profile fields if provided
  if (context.legalName !== undefined && !context.legalName.trim()) {
    errors.push('Full legal name as printed on your government ID is required.');
  }

  const isValid = missingMandatorySides.length === 0 && uploadingSides.length === 0;
  const canSubmit = isValid && (context.isProfileInfoComplete !== false);

  const completionPercentage =
    totalRequiredSides > 0
      ? Math.round((completedSidesCount / totalRequiredSides) * 100)
      : 100;

  // Determine lifecycle status
  const lifecycleStatus = calculateCompanionProfileStatus({
    isValid,
    missingCount: missingMandatorySides.length,
    uploadingCount: uploadingSides.length,
    isProfileInfoComplete: context.isProfileInfoComplete,
    verificationStarted: context.verificationStarted,
    currentAdminVerdict: context.currentAdminVerdict,
  });

  // Generate summary message
  let summaryMessage = 'All mandatory document sides and pages have been completed.';
  if (uploadingSides.length > 0) {
    summaryMessage = `Please wait for ${uploadingSides.length} document upload(s) to finish before submitting.`;
  } else if (missingMandatorySides.length > 0) {
    const sidesList = missingMandatorySides
      .map((item) => `${item.documentTitle} (${item.label})`)
      .join(', ');
    summaryMessage = `Profile submission blocked: Mandatory document completion required. Missing: ${sidesList}.`;
  }

  return {
    canSubmit,
    isValid,
    totalRequiredSides,
    completedSidesCount,
    completionPercentage,
    missingMandatorySides,
    uploadingSides,
    errors,
    summaryMessage,
    lifecycleStatus,
  };
}

/**
 * Enforces completion of all mandatory document sides/pages.
 * Throws a DocumentValidationError if any mandatory side or page is missing or uploading.
 * Useful as a guard in form submission handlers and backend endpoints.
 */
export function enforceMandatoryDocumentSides(
  context: ProfileSubmissionValidationContext
): void {
  const result = validateProfileSubmission(context);
  if (!result.isValid || !result.canSubmit) {
    throw new DocumentValidationError(
      result.summaryMessage,
      result.missingMandatorySides,
      result.uploadingSides,
      result.errors
    );
  }
}

/**
 * Fast boolean check if profile is eligible for submission based on document completion.
 */
export function canSubmitProfile(context: ProfileSubmissionValidationContext): boolean {
  return validateProfileSubmission(context).canSubmit;
}

/**
 * Formats missing document sides into a clean, human-readable bulleted or joined string.
 */
export function formatMissingSidesList(items: MissingDocumentSideItem[]): string {
  if (items.length === 0) return '';
  return items.map((i) => `• ${i.documentTitle}: ${i.label}`).join('\n');
}

/**
 * Export default bundle of utilities for easy import
 */
export const documentValidation = {
  DOCUMENT_REQUIREMENTS_REGISTRY,
  COMPANION_DOCUMENT_REGISTRY,
  normalizeDocumentType,
  normalizeDocumentTypeId,
  getDocumentRequirements,
  isBackSideRequired,
  getMandatoryDocumentSides,
  hasValidUploadedData,
  isPartUploading,
  validateDocumentSides,
  validateCompanionDocuments,
  calculateCompanionProfileStatus,
  resolveActiveDocumentRequirements,
  validateProfileSubmission,
  enforceMandatoryDocumentSides,
  canSubmitProfile,
  formatMissingSidesList,
  DocumentValidationError,
};

export default documentValidation;
